/**
 * 하단 탭 아이콘의 모양. Web·Native 가 **같은 원본**을 읽습니다 (OD-002 —
 * 공유하는 것은 UI 가 아니라 그 아래층입니다).
 *
 * ## 왜 직접 그렸는가
 *
 * 전에는 `⌂ ◈ ◎ ▣ ☺` 유니코드 도형 문자였습니다. 아이콘이 아니고, 기기마다
 * 다르게 그려집니다. 그렇다고 기성 세트(Lucide·Feather)를 가져오면 2px 둥근
 * 선이라는 **지금 어디에나 있는 얼굴**이 됩니다. 그래서 이 앱이 이미 쓰는
 * 언어 — 여권·별·접힌 지도·카테고리 격자 — 로 다섯 개를 그렸습니다.
 *
 * ## 작도 규칙
 *
 * 24×24 격자. 선 굵기 1.75. **납작한 끝(butt)·각진 모서리(miter)** — 둥근
 * 끝의 반대입니다. 비활성은 선만, 활성은 `accent` **한 조각만** 채웁니다.
 * 색만으로 상태를 말하면 색각 이상에서 활성 탭을 찾지 못합니다.
 *
 * 모양을 고칠 때는 24×24 안에서만 고칩니다. 두 플랫폼이 이 좌표계를 그대로
 * 씁니다.
 */

export const ICON_VIEWBOX = "0 0 24 24";
export const ICON_STROKE_WIDTH = 1.75;

export interface IconShape {
  /** 활성일 때 채워지는 한 조각. 비활성일 때도 선으로는 그립니다. */
  readonly accent: string;
  /** 그 밖의 선. 채워지지 않습니다. */
  readonly lines: readonly string[];
}

/** 채움 조각의 불투명도. 선 색을 그대로 옅게 깔아 색을 하나만 씁니다. */
export const ICON_ACCENT_OPACITY = 0.2;

export const TAB_ICONS = {
  /** 지붕 각을 세우고 문을 넓게. 채워지는 조각은 문 — 들어간다는 뜻입니다. */
  home: {
    accent: "M10.2 20.6V14h3.6v6.6z",
    lines: ["M2.6 11 12 3.4 21.4 11", "M5 9.3V20.6h14V9.3"],
  },
  /** 핀이 아니라 접힌 종이 지도. 핀은 누구나 쓰고, 접힌 지도는 들고 다니며 찾는다는 뜻입니다. */
  map: {
    accent: "M9 4.2v13.4l6 2.6V6.8z",
    lines: ["M2.6 6.6 9 4.2l6 2.6 6.4-2.4v13.4L15 20.2l-6-2.6-6.4 2.4z", "M9 4.2v13.4", "M15 6.8v13.4"],
  },
  /** 돋보기가 아닙니다. 탐색 화면이 실제로 카테고리 격자이고, 넷 중 하나만 원 — 찾아낸 것. */
  explore: {
    accent: "M17.4 3.2a3.4 3.4 0 1 1 0 6.8 3.4 3.4 0 1 1 0-6.8z",
    lines: ["M3.6 3.2h6.8v6.8H3.6z", "M3.6 14h6.8v6.8H3.6z", "M14 14h6.8v6.8H14z"],
  },
  /** 테두리 안에 별. 동그란 인장은 어디에나 있지만 별은 최애·덕질의 말입니다. */
  passport: {
    accent: "M12 7.9 13.29 10.22 15.9 10.73 14.09 12.68 14.41 15.32 12 14.2 9.59 15.32 9.91 12.68 8.1 10.73 10.71 10.22z",
    lines: ["M5.2 3.2h13.6v17.6H5.2z"],
  },
  /** 맨 사람이 아니라 여권 사진칸. 각진 테두리가 여권 아이콘과 짝을 이룹니다. */
  me: {
    accent: "M12 6.9a2.9 2.9 0 1 1 0 5.8 2.9 2.9 0 1 1 0-5.8z",
    lines: ["M4 3.4h16v17.2H4z", "M6.9 20.6a5.1 5.1 0 0 1 10.2 0"],
  },
} as const satisfies Record<string, IconShape>;

export type TabIconName = keyof typeof TAB_ICONS;

/* ─────────────────────────────────────────────────────────────────────────
 * 카테고리 아이콘
 *
 * 탭과 **규칙이 일부러 다릅니다.** 탭은 "지금 어디에 있나"(상태)를 말하고
 * 카테고리는 "이게 무엇인가"(정체)를 말합니다. 그리고 카테고리는 지도 핀
 * 안에서 20px 아래까지 줄어듭니다 — 거기서 선은 1px 밑으로 눌려 속이
 * 메워집니다. 그래서 **면으로만** 그립니다. 선 굵기가 없으니 어느 크기로
 * 줄여도 비율이 그대로입니다.
 *
 * 별은 "덕질이 얽힌 곳"이라는 뜻입니다 (여권 탭의 별과 같은 말).
 *   카페 → 콜라보 카페,  일반 장소 → 성지순례
 * 넷을 따로 그리면 사용자가 네 번 외웁니다. 별 하나면 한 번입니다.
 * ───────────────────────────────────────────────────────────────────────── */

export interface IconPart {
  readonly d: string;
  /** 겹치는 부분을 구멍으로 뚫습니다. 한 path 안에 여러 도형을 넣을 때 씁니다. */
  readonly evenOdd?: boolean;
}

/**
 * 절취 홈이 파인 티켓, 가운데 별.
 *
 * 카테고리(공연)와 **행사 일반**이 같은 모양을 씁니다. 이벤트 카드가 이미
 * 티켓 꼴이라 앱 안에서 말이 이어집니다. 두 곳이 같은 상수를 읽으므로
 * 한쪽만 바뀌는 일이 없습니다.
 *
 * 별을 뚫은 이유는 두 가지입니다. 홈 둘과 네모만 있으면 60px 히어로에서
 * 너무 비고, 행사는 덕질이 아니면 갈 일이 없는 자리라 별의 뜻과 맞습니다.
 * 대신 별이 셋(콜라보 카페·성지순례·행사)에 붙게 되었습니다 — 표식이
 * 흔해지면 구별하는 힘이 줄어드므로 **여기서 멈춥니다.**
 */
const TICKET: readonly IconPart[] = [
  { d: "M2.6 6.4h18.8v11.2H2.6Z M2.6 9.8a2.2 2.2 0 1 1 0 4.4 2.2 2.2 0 1 1 0-4.4Z M21.4 9.8a2.2 2.2 0 1 1 0 4.4 2.2 2.2 0 1 1 0-4.4Z M12 8.4 13.13 10.45 15.42 10.89 13.83 12.59 14.12 14.91 12 13.92 9.88 14.91 10.17 12.59 8.58 10.89 10.87 10.45Z", evenOdd: true },
];

/** 카테고리가 없는 **행사**에 쓰는 그림. 장소가 아니라 "때가 있는 것"입니다. */
export const EVENT_ICON = TICKET;

export const CATEGORY_ICONS: Readonly<Record<string, readonly IconPart[]>> = {
  /** 차양 아래 문 하나. 천막이 아니라 잠깐 서는 가게입니다. */
  POPUP_STORE: [
    { d: "M4.4 4.4h15.2l2.4 5.6H2z" },
    { d: "M4.6 11h14.8v10H4.6Z M9.8 14.8h4.4v6.2H9.8Z", evenOdd: true },
  ],
  /** 손잡이 달린 컵에 별. 별 하나가 카페와 콜라보를 가릅니다. */
  COLLAB_CAFE: [
    { d: "M3.2 9.6h11.2l-1.3 8.3a1.9 1.9 0 0 1-1.9 1.6H6.4a1.9 1.9 0 0 1-1.9-1.6z" },
    { d: "M15.8 9.9a3.1 3.1 0 1 1 0 6.2 3.1 3.1 0 1 1 0-6.2Z M15.8 11.35a1.65 1.65 0 1 1 0 3.3 1.65 1.65 0 1 1 0-3.3Z", evenOdd: true },
    { d: "M1.8 21h14.2v1.8H1.8z" },
    { d: "M19 2 20 3.83 22.04 4.21 20.62 5.73 20.88 7.79 19 6.9 17.12 7.79 17.38 5.73 15.96 4.21 18 3.83Z" },
  ],
  /** 손잡이 달린 쇼핑백. 사서 들고 나오는 곳입니다. */
  GOODS_STORE: [
    { d: "M8.4 8V6.2a3.6 3.6 0 0 1 7.2 0V8h-1.9V6.2a1.7 1.7 0 0 0-3.4 0V8z" },
    { d: "M4.5 8.6h15l-1.2 12a1.7 1.7 0 0 1-1.7 1.5H7.4a1.7 1.7 0 0 1-1.7-1.5z" },
  ],
  /** 캡슐이 든 둥근 통, 아래는 배출구. 안이 비면 그냥 공입니다. */
  GACHA_STORE: [
    { d: "M12 2.6a5.4 5.4 0 1 1 0 10.8 5.4 5.4 0 1 1 0-10.8Z M9.9 5.15a1.75 1.75 0 1 1 0 3.5 1.75 1.75 0 1 1 0-3.5Z M14.1 4.75a1.75 1.75 0 1 1 0 3.5 1.75 1.75 0 1 1 0-3.5Z M12 8.55a1.75 1.75 0 1 1 0 3.5 1.75 1.75 0 1 1 0-3.5Z", evenOdd: true },
    { d: "M5 12.8h14v8.6H5Z M8.4 17.6h7.2v2.6H8.4Z", evenOdd: true },
  ],
  /**
   * 사람 둘. 팬미팅·팬사인회는 결국 만나는 자리입니다.
   *
   * 응원봉으로 그렸다가 물렸습니다. 팬덤의 말로는 정확한데 모르는 사람에겐
   * 막대일 뿐이고, 가챠 기계와 구조가 닮아 작은 크기에서 헷갈립니다.
   * 지도 핀은 0.3초 보는 자리라 정확함보다 알아보는 것이 먼저입니다.
   */
  FAN_EVENT: [
    { d: "M8.4 7.3a2.9 2.9 0 1 1 0 5.8 2.9 2.9 0 1 1 0-5.8z" },
    { d: "M16 7.3a2.9 2.9 0 1 1 0 5.8 2.9 2.9 0 1 1 0-5.8z" },
    { d: "M2.6 20.6a5.8 5.8 0 0 1 11.6 0z" },
    { d: "M10.2 20.6a5.8 5.8 0 0 1 11.6 0z" },
  ],
  /** 장소에 별. 필름으로 그리면 애니가 아닌 성지가 빠집니다. */
  HOLY_PLACE: [
    { d: "M12 2.2a7.4 7.4 0 0 1 7.4 7.4c0 5.5-7.4 12.2-7.4 12.2S4.6 15.1 4.6 9.6A7.4 7.4 0 0 1 12 2.2Z M12 6.2 13.06 8.14 15.23 8.55 13.71 10.16 14 12.35 12 11.4 10 12.35 10.29 10.16 8.77 8.55 10.94 8.14Z", evenOdd: true },
  ],
  /** 벽에 걸린 두 폭. 한 장이면 사진, 두 장이면 전시입니다. */
  EXHIBITION: [
    { d: "M2.4 3.6h19.2v1.6H2.4z" },
    { d: "M4.8 6.4h6.2v10.4H4.8z" },
    { d: "M13 6.4h6.2v13.6H13z" },
  ],
  /** 절취 홈이 파인 티켓. 행사 일반과 같은 모양을 씁니다. */
  CONCERT: TICKET,
  /** 손잡이 달린 컵, 별은 없음. 그냥 카페입니다. */
  CAFE: [
    { d: "M3.6 8.4h12.4l-1.4 9.2a2 2 0 0 1-2 1.7H7a2 2 0 0 1-2-1.7z" },
    { d: "M17.4 8.6a3.4 3.4 0 1 1 0 6.8 3.4 3.4 0 1 1 0-6.8Z M17.4 10.2a1.8 1.8 0 1 1 0 3.6 1.8 1.8 0 1 1 0-3.6Z", evenOdd: true },
    { d: "M2.2 20.6h15.2v1.9H2.2z" },
  ],
  /** 별 없는 핀. 분류를 모를 때 여기로 옵니다. */
  DEFAULT: [
    { d: "M12 2.2a7.4 7.4 0 0 1 7.4 7.4c0 5.5-7.4 12.2-7.4 12.2S4.6 15.1 4.6 9.6A7.4 7.4 0 0 1 12 2.2Z M12 6.6a3 3 0 1 1 0 6 3 3 0 1 1 0-6Z", evenOdd: true },
  ],
};
